import { safeFetch } from '../utils/requestUtils';
import { dbtConfig } from '../config/dbt';
import { getSession } from '../api/supabase';

/**
 * dbt API Service
 * Handles all API calls related to dbt Core and Cloud integration
 */
class DbtService {
  constructor() {
    this.baseURL = process.env.REACT_APP_API_URL || '';
    this.defaultHeaders = {
      'Content-Type': 'application/json',
    };
  }

  /**
   * Get authorization headers
   */
  async getAuthHeaders() {
    try {
      const session = await getSession();
      const token = session?.access_token;
      return token ? { Authorization: `Bearer ${token}` } : {};
    } catch (error) {
      console.error('Error getting auth session:', error);
      return {};
    }
  }

  /**
   * Handle authentication errors
   */
  handleAuthError(error) {
    if (error.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');

      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    throw error;
  }

  /**
   * Enhanced fetch wrapper
   */
  async fetch(endpoint, options = {}) {
    const authHeaders = await this.getAuthHeaders();
    const headers = {
      ...this.defaultHeaders,
      ...authHeaders,
      ...options.headers
    };

    try {
      return await safeFetch(endpoint, {
        ...options,
        headers
      });
    } catch (error) {
      if (error.status === 401 || error.status === 403) {
        return this.handleAuthError(error);
      }
      throw error;
    }
  }

  // ============================================================================
  // CONNECTION MANAGEMENT
  // ============================================================================

  /**
   * Get all dbt connections for the current user
   */
  async getConnections() {
    const response = await this.fetch(dbtConfig.endpoints.connections, {
      method: 'GET'
    });
    return response.connections || [];
  }

  /**
   * Create a new dbt connection
   */
  async createConnection(connectionData) {
    const response = await this.fetch(dbtConfig.endpoints.connections, {
      method: 'POST',
      body: JSON.stringify(connectionData)
    });
    return response.connection;
  }

  /**
   * Get a specific dbt connection by ID
   */
  async getConnection(connectionId) {
    const response = await this.fetch(`${dbtConfig.endpoints.connections}/${connectionId}`, {
      method: 'GET'
    });
    return response.connection;
  }

  /**
   * Update a dbt connection
   */
  async updateConnection(connectionId, connectionData) {
    const response = await this.fetch(`${dbtConfig.endpoints.connections}/${connectionId}`, {
      method: 'PUT',
      body: JSON.stringify(connectionData)
    });
    return response.connection;
  }

  /**
   * Delete a dbt connection
   */
  async deleteConnection(connectionId) {
    const response = await this.fetch(`${dbtConfig.endpoints.connections}/${connectionId}`, {
      method: 'DELETE'
    });
    return response;
  }

  /**
   * Validate a dbt connection
   */
  async validateConnection(connectionId) {
    const response = await this.fetch(
      `${dbtConfig.endpoints.connections}/${connectionId}/validate`,
      { method: 'POST' }
    );
    return response;
  }

  /**
   * Process dbt Core artifacts for a connection
   */
  async processArtifacts(connectionId, artifactsPath) {
    const response = await this.fetch(
      `${dbtConfig.endpoints.connections}/${connectionId}/process-artifacts`,
      {
        method: 'POST',
        body: JSON.stringify({ artifacts_path: artifactsPath })
      }
    );
    return response;
  }

  /**
   * Validate dbt artifacts at a specified path
   */
  async validateArtifacts(artifactsPath) {
    const response = await this.fetch(dbtConfig.endpoints.validateArtifacts, {
      method: 'POST',
      body: JSON.stringify({ artifacts_path: artifactsPath })
    });
    return response.validation_results;
  }

  // ============================================================================
  // PROJECT DATA
  // ============================================================================

  /**
   * Get all models for a project
   */
  async getProjectModels(projectId) {
    const response = await this.fetch(
      `${dbtConfig.endpoints.projects}/${projectId}/models`,
      { method: 'GET' }
    );
    return response.models || [];
  }

  /**
   * Get all tests for a project
   */
  async getProjectTests(projectId) {
    const response = await this.fetch(
      `${dbtConfig.endpoints.projects}/${projectId}/tests`,
      { method: 'GET' }
    );
    return response.tests || [];
  }

  /**
   * Get project overview analytics
   */
  async getProjectOverview(projectId) {
    const response = await this.fetch(
      `${dbtConfig.endpoints.projects}/${projectId}/analytics/overview`,
      { method: 'GET' }
    );
    return response.overview;
  }

  /**
   * Get latest run results for a project
   */
  async getLatestRun(projectId) {
    const response = await this.fetch(
      `${dbtConfig.endpoints.projects}/${projectId}/latest-run`,
      { method: 'GET' }
    );
    return response.latest_run;
  }

  /**
   * Get failed tests for a project
   */
  async getFailedTests(projectId, runId = null) {
    const params = runId ? `?run_id=${runId}` : '';
    const response = await this.fetch(
      `${dbtConfig.endpoints.projects}/${projectId}/failed-tests${params}`,
      { method: 'GET' }
    );
    return response.failed_tests || [];
  }

  // ============================================================================
  // LINEAGE AND ANALYTICS
  // ============================================================================

  /**
   * Get lineage for a specific model
   */
  async getModelLineage(projectId, modelUniqueId) {
    const encodedId = encodeURIComponent(modelUniqueId);
    const response = await this.fetch(
      `${dbtConfig.endpoints.projects}/${projectId}/models/${encodedId}/lineage`,
      { method: 'GET' }
    );
    return response.lineage;
  }

  /**
   * Get execution history for a specific test
   */
  async getTestHistory(projectId, testUniqueId, limit = 10) {
    const encodedId = encodeURIComponent(testUniqueId);
    const response = await this.fetch(
      `${dbtConfig.endpoints.projects}/${projectId}/tests/${encodedId}/history?limit=${limit}`,
      { method: 'GET' }
    );
    return response.history || [];
  }

  // ============================================================================
  // HEALTH CHECK
  // ============================================================================

  /**
   * Check dbt integration health
   */
  async healthCheck() {
    const response = await this.fetch(dbtConfig.endpoints.health, {
      method: 'GET'
    });
    return response;
  }
}

// Export singleton instance
const dbtService = new DbtService();
export default dbtService;
